<template>
  <div class="step-tab">
    <div class="cp-card">
      <!-- Box visibility for this step (reflected in the preview below). -->
      <BoxVisibilityToggle
          :boxVisibility="profile.box_visibility"
          boxKey="payment_schedule"
          :label="L.review_box_payment_schedule || 'Price overview & schedule'"
          :hint="L.box_toggle_payment_schedule_hint || 'Price breakdown + deposit/balance schedule.'" />
      <BoxVisibilityToggle
          :boxVisibility="profile.box_visibility"
          boxKey="payment_confirm"
          :label="L.review_box_payment_confirm || 'Payment terms confirmation'"
          :hint="L.box_toggle_payment_confirm_hint || 'Required confirmation checkbox before booking.'" />

      <!-- Stripe card payment: same box as the toggles above, but controlled — turning
           it ON opens the key modal (cannot enable without a validated key); turning it
           OFF asks to confirm, then deletes the stored key. The switch reflects the real
           stored state via stripeOn. -->
      <BoxVisibilityToggle
          controlled
          :value="stripeOn"
          :disabled="!stripeEnabledGlobal"
          :label="L.review_box_stripe || 'Stripe payment'"
          :hint="!stripeEnabledGlobal
            ? (L.stripe_coming_soon || 'Coming soon')
            : (stripeStatus.exists
              ? ((L.stripe_toggle_hint_active || 'Active —') + ' ' + maskedStripeLabel)
              : (L.stripe_toggle_hint || 'Let travelers pay by card via this brand’s own Stripe account.'))"
          @toggle="onStripeToggle" />

      <StripeKeyModal
          :show="stripeModal"
          :options="options"
          :profileId="profileId"
          :status="stripeStatus"
          :brandColor="brandColor"
          @saved="onStripeSaved"
          @cancel="stripeModal = false" />

      <div class="cp-grid">
        <CustomInput :label="L.checkout_text_payment_label || 'Payment method label'" v-model="text.checkout_payment_label" />
      </div>

      <div class="cp-richfield">
        <label class="cp-label">{{ L.checkout_text_payment_confirm || 'Payment confirmation text' }}</label>
        <div class="xi-editor">
          <TextEditorBox class="branding-tabs" :options="options" :editor="editor" :image="true" />
          <editor-content class="xi-editor-content" :editor="editor" />
        </div>
      </div>
    </div>

    <PreviewFrame :label="L.preview || 'Preview'">
      <PaymentPreview :texts="text" :rich="richHtml" :lang="lang" :brandColor="brandColor" :boxVisibility="profile.box_visibility" />
    </PreviewFrame>
  </div>
</template>

<script>
import { EditorContent } from '@tiptap/vue-2'
import TextEditorBox from '@/components/basics/TextEditorBox.vue'
import CustomInput from '../../parts/CustomInput.vue'
import PreviewFrame from './previews/PreviewFrame.vue'
import PaymentPreview from './previews/PaymentPreview.vue'
import BoxVisibilityToggle from './BoxVisibilityToggle.vue'
import StripeKeyModal from '../../../components/modals/StripeKeyModal.vue'

/**
 * Payment step tab: edits the payment-method label + payment confirmation text,
 * with a live PaymentPreview below. The parent owns `text` (texts[lang]) and the
 * rich editor instance; this tab binds them and surfaces live editor HTML for
 * the preview via the `richHtml` snapshot the parent keeps refreshed.
 *
 * Also hosts the Stripe card-payment toggle: a guarded switch that opens the
 * StripeKeyModal to validate+store the brand's Stripe key (on) or confirms before
 * deleting it (off). Stripe state is owned by the backend; this tab reflects the
 * parent-provided `stripeStatus` and reports changes back via events.
 */
export default {
  name: 'StepTabPayment',
  components: { EditorContent, TextEditorBox, CustomInput, PreviewFrame, PaymentPreview, BoxVisibilityToggle, StripeKeyModal },
  props: {
    options: { type: Object, required: true },
    profile: { type: Object, required: true },      // holds box_visibility (mutated in place)
    text: { type: Object, required: true },        // texts[lang] (mutated in place)
    editor: { type: Object, default: null },        // payment-confirm tiptap editor
    richHtml: { type: Object, default: () => ({}) },// live { fieldKey: html } from parent
    lang: { type: String, default: 'de' },
    brandColor: { type: String, default: '#1a73e8' },
    // Persisted profile id (null until the profile has been saved once).
    profileId: { type: [Number, String], default: null },
    // Masked Stripe key status from the parent: { exists, type, last4 }.
    stripeStatus: { type: Object, default: () => ({ exists: false, type: null, last4: null }) },
    // Global feature flag (SuperAdmin). When false, Stripe is "coming soon" — the toggle
    // is disabled but stored keys are untouched.
    stripeEnabledGlobal: { type: Boolean, default: false }
  },
  data() {
    return { stripeModal: false };
  },
  computed: {
    L() { return this.options.Lang || {}; },
    stripeOn() { return !!(this.stripeStatus && this.stripeStatus.exists); },
    maskedStripeLabel() {
      const type = this.stripeStatus && this.stripeStatus.type;
      const last4 = (this.stripeStatus && this.stripeStatus.last4) || '';
      const typeLabel = type === 'live'
        ? (this.L.stripe_key_live || 'Live key')
        : (type === 'test' ? (this.L.stripe_key_test || 'Test key') : (this.L.stripe_key_generic || 'Key'));
      return `${typeLabel} · ••••${last4}`;
    }
  },
  methods: {
    onStripeToggle(val) {
      if (val) {
        // Enabling: a profile must exist first (key endpoints need its id), then the
        // modal owns validation + save. The switch only turns on once a key is saved.
        if (!this.profileId) {
          this.showStripeError(
            this.L.stripe_modal_save_profile_first || 'Please save the profile once before adding a Stripe key.'
          );
          return;
        }
        this.stripeModal = true;
      } else {
        // Disabling deletes the stored key — confirm via the global modal first.
        this.confirmDisableStripe();
      }
    },
    onStripeSaved(meta) {
      this.stripeModal = false;
      this.$emit('stripe-changed', meta); // parent updates stripeStatus
    },
    confirmDisableStripe() {
      const self = this;
      const L = this.L;
      this.$set(this.options, 'globalConfirm', {
        visible: true,
        title: L.stripe_disable_title || 'Disable Stripe payment?',
        message: L.stripe_disable_message
          || 'This permanently deletes the stored Stripe key for this profile. To use Stripe again you will have to enter and validate the key anew. Continue?',
        confirmBtnText: L.stripe_disable_confirm || 'Disable & delete key',
        onCancel() { self.options.globalConfirm.visible = false; },
        onConfirm() {
          self.options.globalConfirm.visible = false;
          self.doPost({ data: { id: self.profileId } }, 'checkout-profiles/stripe-key/disable', function (res) {
            // Envelope status is 'ok'|'error' (not 'success'); error iff 'error'.
            if (res && res.status !== 'error') {
              self.$emit('stripe-changed', { exists: false, type: null, last4: null });
            } else {
              self.showStripeError(L.stripe_disable_failed || 'Could not disable Stripe. Please try again.');
            }
          });
        }
      });
    },
    showStripeError(message) {
      const self = this;
      const L = this.L;
      this.$set(this.options, 'globalConfirm', {
        visible: true,
        title: L.error || 'Error',
        message,
        confirmBtnText: L.ok || 'OK',
        onCancel() { self.options.globalConfirm.visible = false; },
        onConfirm() { self.options.globalConfirm.visible = false; }
      });
    }
  }
};
</script>

<style scoped>
.step-tab { animation: cp-fade .2s ease; }
@keyframes cp-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }

.cp-card { background: #fff; border-radius: 1rem; box-shadow: 0 2px 12px rgba(0,0,0,0.06); padding: 1.5rem 1.75rem; }
.cp-label { display: block; font-size: 0.78rem; font-weight: 600; color: #555; margin-bottom: 0.25rem; }
.cp-input { width: 100%; border: 1px solid #dcdcdc; border-radius: 8px; padding: 0.55rem 0.75rem; font-size: 0.9rem; background: #fafafa; box-sizing: border-box; }
.cp-input:focus-within, .cp-input:focus { outline: none; border-color: #999; background: #fff; }
.cp-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem 1.5rem; }
@media (max-width: 800px) { .cp-grid { grid-template-columns: 1fr; } }
.cp-field { display: flex; flex-direction: column; min-width: 0; }
.cp-richfield { margin-top: 1.25rem; }
</style>
<!-- Shared rich-editor box look — matches Edit Branding ▸ Additional Information. -->
<style lang="scss">
@use "../../../assets/style/General/richEditorBox.scss";
</style>
